export default (complimentaryProduct, thresholdReferencePrice) => ({
    open: false,
    items: [],
    totalPrice: 0,
    totalDiscount: 0,
    loading: false,
    loadingItemId: null,
    complimentaryProductThresholdMet: false,
    complimentaryProductDismissed: Alpine.$persist(false).using(sessionStorage),

    init() {
        this.fetchItems()

        window.addEventListener('cart:addItem', (e) => {
            const { id, handle, price, quantity, el } = e.detail
            this.addItem(id, quantity, el)

            if (typeof pixlee_analytics !== 'undefined') {
                pixlee_analytics.events.trigger('add:to:cart', {
                    'product_sku': handle,
                    'price': (price / 100).toFixed(2),
                    'quantity': quantity,
                    'currency': shopifyData.currency.iso_code
                })
            }
        })
    },

    toggle() {
        this.open = !this.open;
        document.body.classList.toggle('overflow-hidden');
    },
    close() {
        this.open = false;
        document.body.classList.remove('overflow-hidden');
    },
    async fetchItems(openDrawer = false, transformCallback = null) {
        try {
            const response = await fetch('/cart.js', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            const data = await response.json()
            
            let items = data.items
            this.totalPrice = data.total_price
            this.totalDiscount = data.total_discount
            this.updateCartIconCount(data.item_count)

            this.$nextTick(() => this.checkComplimentaryProductCriteria())

            // Fetches each cart item's variant data to populate the variant switcher
            if (items.length) {
                const variantIds = items.map(item => 
                    `gid://shopify/ProductVariant/${item.variant_id}`
                )
                
                const variantDataQuery = `
                    query getVariants($ids: [ID!]!) {
                        nodes(ids: $ids) {
                            ... on ProductVariant {
                                id
                                selectedOptions {
                                    name
                                    value
                                }
                                product {
                                    variants(first: 100) {
                                        nodes {
                                            id
                                            availableForSale
                                            selectedOptions {
                                                name
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `
                
                try {
                    const storefrontResponse = await fetch('/api/2024-01/graphql.json', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Shopify-Storefront-Access-Token': shopifyData.storefrontApi.token
                        },
                        body: JSON.stringify({
                            query: variantDataQuery,
                            variables: { ids: variantIds }
                        })
                    })
                    
                    const result = await storefrontResponse.json()
                    
                    if (result.data?.nodes) {
                        // Store variant data for each item
                        items = items.map((item, index) => {
                            const node = result.data.nodes[index]
                            let variants = []

                            if (node?.product?.variants?.nodes) {
                                variants = node.product.variants.nodes.flatMap(variant =>
                                    variant.selectedOptions.map(option => ({
                                        id: parseInt(variant.id.split('/').at(-1)),
                                        name: option.name,
                                        value: option.value,
                                        availableForSale: variant.availableForSale
                                    }))
                                )
                            }

                            return {
                                ...item,
                                variants,
                            }
                        })
                    }
                } catch (error) {
                    console.error('Error fetching variant data:', error)
                }
            }
            
            // Apply transform callback if provided (after all data processing)
            if (transformCallback) {
                items = transformCallback(items)
            }
            
            // Set this.items only once after all processing is complete
            this.items = items

            if (openDrawer) {
                this.toggle()
            }
        } catch (error) {
            console.error('Error:', error)
        }
    },
    async swapVariant(oldVariantId, newVariantId, quantity, originalIndex) {
        // Ensure IDs are numbers for consistent comparison
        newVariantId = Number(newVariantId)
        oldVariantId = Number(oldVariantId)
        
        // Track the item that should come BEFORE the swapped item for repositioning
        const itemBeforeId = originalIndex > 0 ? this.items[originalIndex - 1].variant_id : null
        
        this.loading = true
        this.loadingItemId = oldVariantId

        try {
            // 1. Remove the old variant from the cart
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    id: oldVariantId.toString(), 
                    quantity: 0 
                }),
            })

            // 2. Add the new variant to the cart
            const addResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    id: newVariantId, 
                    quantity: Number(quantity) 
                }),
            })

            if (!addResponse.ok && addResponse.status === 422) {
                const data = await addResponse.json()
                
                // Rollback: restore the original variant
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        id: oldVariantId, 
                        quantity: Number(quantity) 
                    }),
                })
                
                throw new Error(data.message)
            }

            // 3. Re-fetch cart with reordering callback to prevent flicker
            await this.fetchItems(false, (items) => {
                // Find the newly added item and move it to the correct position
                const newItemIndex = items.findIndex(item => item.variant_id === newVariantId)
                
                if (newItemIndex !== -1) {
                    // Remove the item from its current position
                    const [newItem] = items.splice(newItemIndex, 1)
                    
                    // Calculate target position based on the item that should come before it
                    let targetIndex = 0
                    if (itemBeforeId) {
                        const beforeIndex = items.findIndex(item => item.variant_id === itemBeforeId)
                        targetIndex = beforeIndex !== -1 ? beforeIndex + 1 : items.length
                    }
                    
                    // Insert at the target position  
                    items.splice(targetIndex, 0, newItem)
                }
                
                return items
            })

        } catch (error) {
            console.error('Error swapping variant:', error)
            // Re-fetch to ensure cart state is accurate
            await this.fetchItems()
        } finally {
            this.loading = false
            this.loadingItemId = null
        }
    },
    addItem(id, quantity = 1, openDrawer = true) {
        this.loading = true

        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: Number(id),
                quantity: Number(quantity),
            }),
        })
            .then(async (response) => {
                if (!response.ok && response.status === 422) {
                    const data = await response.json()
                    throw new Error(data.message)
                }

                return response.json()
            })
            .then((data) => {
                this.fetchItems(openDrawer)
            })
            .catch((error) => console.error('Error:', error))
            .finally(() => (this.loading = false))
    },
    updateItem(id, quantity, event) {
        const target = event.target
        this.loading = true
        this.loadingItemId = id

        fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                // The Cart API is inconsistent and requires the id to be a
                // string for the change endpoint
                id: id.toString(),
                quantity: Number(quantity),
            }),
        })
            .then(async (response) => {
                if (!response.ok && response.status === 422) {
                    const data = await response.json()
                    this.lineItemError(
                        data.description,
                        target.closest('article')
                    )
                    throw new Error(data.message)
                }

                return response.json()
            })
            .then((data) => {
                const previousItems = this.items
                this.items = data.items
                    .filter((item) => item.quantity > 0)
                    .map((item) => {
                        const prev = previousItems.find((i) => i.variant_id === item.variant_id)
                        return prev ? { ...item, variants: prev.variants } : item
                    })
                this.totalPrice = data.total_price
                this.totalDiscount = data.total_discount
                this.updateCartIconCount(data.item_count)

                this.$nextTick(() => this.checkComplimentaryProductCriteria())
            })
            .catch((error) => console.error(error))
            .finally(() => {
                this.loading = false
                this.loadingItemId = null
            })
    },
    lineItemError(message, parentTarget) {
        const target = parentTarget.querySelector('[data-error-wrapper')

        if (target.querySelector('.line-item-error')) {
            return
        }

        const element = document.createElement('p')
        element.classList.add(
            'text-sm',
            'text-red-400',
            'line-item-error'
        )
        element.textContent = message

        target.appendChild(element)
    },
    updateCartIconCount(count) {
        const itemCount = document.getElementById('cart-item-count')
        itemCount.textContent = count
    },
    checkComplimentaryProductCriteria() {
        if (!complimentaryProduct || !thresholdReferencePrice) {
            this.complimentaryProductThresholdMet = false
            return
        }

        this.complimentaryProductThresholdMet = this.totalPrice >= thresholdReferencePrice
    }
})
